import React, { useCallback, useEffect, useRef, useState } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { CallIntelSearchField, generateTrackingId } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { useIsSmallMode } from '@frontend/shared';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Button, useDebouncedValue } from '@frontend/design-system';
import { trackingIds } from '../../../../tracking-ids';
import { SuggestedSearches } from './suggested-searches';

const DEBOUNCE_DELAY_MS = 500;
const MIN_SEARCH_LENGTH = 3;

type ExpandableToggleProps = {
  isExpanded: boolean;
  onCollapse: () => void;
  onExpand: () => void;
};

type TranscriptSearchFilterProps = {
  expandableMode?: {
    isExpanded: boolean;
    onExpandStateChange: (isExpanded: boolean) => void;
  };
  onActiveChange?: (isActive: boolean) => void;
  onChange?: (value: string, debounced?: boolean) => void;
  onSearchTypeChange: (searchType: CallIntelTypes.SearchTypeEnum) => void;
  placeholder?: string;
  searchType: CallIntelTypes.SearchTypeEnum;
  trackingId?: string;
  value?: string;
  wrapperStyles?: SerializedStyles;
};

const getInputElement = (container: HTMLDivElement | null) => container?.querySelector('input');

export const TranscriptSearchFilter: React.FC<TranscriptSearchFilterProps> = ({
  expandableMode,
  onActiveChange,
  onChange: onSearchChange,
  placeholder,
  trackingId = trackingIds.callIntel.transcriptSearchInput,
  value,
  wrapperStyles,
  searchType,
  onSearchTypeChange,
}) => {
  const { t } = useTranslation('call-intelligence');
  const { isSmallMode } = useIsSmallMode();

  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? '');
  const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchTypeMenuRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebouncedValue(inputValue, DEBOUNCE_DELAY_MS);

  const isSearchVisible = !expandableMode || expandableMode.isExpanded;

  const trimmedInputValue = inputValue.trim();
  const isSearchQueryEmpty = !trimmedInputValue;
  const hasSearchLengthError = !isSearchQueryEmpty && trimmedInputValue.length < MIN_SEARCH_LENGTH;
  const searchLengthErrorMessage = t('Type at least 3 characters');

  const deactivateSearch = useCallback(() => {
    setIsFocused(false);
    setIsSuggestionsVisible(false);
  }, []);

  const isInsideSearchLayer = useCallback((target: EventTarget | null) => {
    if (!target) return false;

    const elements = [containerRef.current, searchTypeMenuRef.current].filter(Boolean);

    return elements.some((element) => {
      if (!element) return false;

      return target instanceof Node && element.contains(target);
    });
  }, []);

  // Collapse when focus leaves the entire container (click outside)
  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      const nextFocusedElement = e.relatedTarget;

      if (!nextFocusedElement) {
        return;
      }

      if (isInsideSearchLayer(nextFocusedElement)) {
        return;
      }

      deactivateSearch();

      if (!isSmallMode) {
        onActiveChange?.(false);
      }
    },
    [deactivateSearch, isSmallMode, onActiveChange, isInsideSearchLayer]
  );

  const handleClear = useCallback(() => {
    setInputValue('');
    onSearchChange?.('');
    if (isFocused) {
      setIsSuggestionsVisible(true);
    }
  }, [onSearchChange, isFocused]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = e.target.value;

      setInputValue(nextValue);

      if (nextValue === '') {
        onSearchChange?.('');
      }
    },
    [onSearchChange]
  );

  // Small mode Done button — deactivates search and collapses
  const handleCollapse = useCallback(() => {
    onActiveChange?.(false);
    expandableMode?.onExpandStateChange(false);
    deactivateSearch();
  }, [deactivateSearch, onActiveChange, expandableMode]);

  const handleExpand = useCallback(() => {
    expandableMode?.onExpandStateChange(true);
  }, [expandableMode]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && inputValue.length >= MIN_SEARCH_LENGTH) {
        onSearchChange?.(inputValue, true);
      }
    },
    [onSearchChange, inputValue]
  );

  const handleSearchFieldClick = useCallback(() => {
    setIsFocused(true);
    setIsSuggestionsVisible(true);
    getInputElement(containerRef.current)?.focus();
    // On desktop, activate on click (hides chips). On small mode, already active.
    if (!isSmallMode) {
      onActiveChange?.(true);
    }
  }, [isSmallMode, onActiveChange]);

  const handleSuggestedSearchSelect = useCallback(
    (query: string) => {
      setInputValue(query);
      onSearchChange?.(query, true);
      deactivateSearch();
      getInputElement(containerRef.current)?.blur();
      // On desktop, deactivate after selecting a suggestion
      if (!isSmallMode) {
        onActiveChange?.(false);
      }

      pendo.track(
        generateTrackingId({ component: 'transcript-search', context: isSmallMode ? 'small-mode-click' : 'click' })
      );
    },
    [deactivateSearch, onSearchChange, isSmallMode, onActiveChange]
  );

  const handleSuggestionsMouseDown = useCallback((e: React.MouseEvent) => e.preventDefault(), []);

  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  // Fire debounced search + track analytics
  useEffect(() => {
    if (debouncedQuery && debouncedQuery.length >= MIN_SEARCH_LENGTH) {
      onSearchChange?.(debouncedQuery, true);

      pendo.track(`${trackingId}:${trackingIds.callIntel.searchQuerySuffix}`, {
        searchText: debouncedQuery,
      });
    }
  }, [debouncedQuery, onSearchChange, trackingId]);

  return (
    <div
      aria-label={t('Transcript search')}
      css={[styles.container({ isExpandableMode: expandableMode?.isExpanded ?? false }), wrapperStyles]}
      onBlur={handleBlur}
      ref={containerRef}
      role='search'
    >
      {isSearchVisible && (
        <div css={styles.searchWrapper} onClick={handleSearchFieldClick}>
          <CallIntelSearchField
            clearBtnTrackingId={generateTrackingId({
              component: 'transcript-search',
              context: isSmallMode ? 'small-mode-clear-btn-click' : 'clear-btn-click',
            })}
            hasValue={!!inputValue}
            hasError={hasSearchLengthError}
            isFocused={isFocused}
            onChange={handleInputChange}
            onClear={handleClear}
            onKeyDown={handleKeyDown}
            placeholder={placeholder ?? t('Find calls about...')}
            value={inputValue}
            trackingId={generateTrackingId({
              component: 'transcript-search',
              context: isSmallMode ? 'small-mode-click' : 'click',
            })}
          />
        </div>
      )}

      {/* Suggestions dropdown — mouseDown prevents blur from closing it */}
      {isSuggestionsVisible && (
        <div css={styles.suggestionsDropdown} onMouseDown={handleSuggestionsMouseDown}>
          <SuggestedSearches
            isSmallMode={isSmallMode}
            isSearchQueryEmpty={isSearchQueryEmpty}
            searchType={searchType}
            hasError={hasSearchLengthError}
            errorMessage={searchLengthErrorMessage}
            searchTypeMenuRef={searchTypeMenuRef}
            onSelect={handleSuggestedSearchSelect}
            onSearchTypeChange={onSearchTypeChange}
          />
        </div>
      )}

      {!!expandableMode && (
        <ExpandableToggle isExpanded={expandableMode.isExpanded} onCollapse={handleCollapse} onExpand={handleExpand} />
      )}
    </div>
  );
};

const ExpandableToggle: React.FC<ExpandableToggleProps> = ({ isExpanded, onCollapse, onExpand }) => {
  const { t } = useTranslation('call-intelligence');

  if (isExpanded) {
    return (
      <Button
        onClick={onCollapse}
        size='large'
        trackingId={trackingIds.callIntel.searchCollapseTrigger}
        variant='secondary'
      >
        {t('Done')}
      </Button>
    );
  }

  return (
    <Button
      aria-label={t('Open Search')}
      css={styles.expandSearchButton}
      iconName='search'
      onClick={onExpand}
      size='large'
      trackingId={trackingIds.callIntel.searchExpandTrigger}
      variant='secondary'
    />
  );
};

const styles = {
  container: ({ isExpandableMode }: { isExpandableMode: boolean }) => css`
    display: flex;
    gap: ${theme.spacing(1)};
    min-height: 40px;
    position: relative;
    ${isExpandableMode ? 'flex: 1;' : ''}

    & > div:first-child {
      display: flex;
      flex-direction: column;
    }
  `,

  expandSearchButton: css`
    border: 1px solid ${theme.colors.neutral50};
  `,

  searchWrapper: css`
    flex: 1;
    min-width: 0;
    position: relative;
    display: flex;
    flex-direction: column;
  `,

  suggestionsDropdown: css`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: ${theme.zIndex.popover};
  `,
};
